/**
 * Account Service - API functions for account management
 */

import { authService } from './authService';
import { logger } from '../utils/logger';
import type {
  AccountProfile,
  UpdateAccountRequest,
  ProfilePictureUploadUrlResponse,
  FinalizeProfilePictureResponse,
  DeleteAccountResponse,
} from '../shared/account-types';

const API_BASE_URL = import.meta.env.VITE_CORPUS_API_BASE_URL || '';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
}

/**
 * Make an authenticated API request
 */
async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const token = authService.getToken();

    if (!token) {
      return {
        success: false,
        error: 'Not authenticated',
        code: 'NOT_AUTHENTICATED'
      };
    }

    const headers = new Headers(options.headers);
    headers.set('Authorization', `Bearer ${token}`);
    headers.set('Content-Type', 'application/json');

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers
    });

    // Handle 401 Unauthorized
    if (response.status === 401) {
      authService.clearToken();
      const fullPath = window.location.pathname + window.location.search + window.location.hash;
      window.location.href = `/login?redirect=${encodeURIComponent(fullPath)}`;
      return {
        success: false,
        error: 'Session expired',
        code: 'UNAUTHORIZED'
      };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.detail || errorData.message || `HTTP ${response.status}`,
        code: errorData.code || `HTTP_${response.status}`
      };
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return { success: true };
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error: unknown) {
    logger.error('Account API request failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
      code: 'NETWORK_ERROR'
    };
  }
}

/**
 * Get current user's account profile
 */
export async function getAccount(): Promise<ApiResponse<AccountProfile>> {
  return fetchWithAuth<AccountProfile>('/api/account');
}

/**
 * Update current user's account profile
 */
export async function updateAccount(
  data: UpdateAccountRequest
): Promise<ApiResponse<AccountProfile>> {
  return fetchWithAuth<AccountProfile>('/api/account', {
    method: 'PATCH',
    body: JSON.stringify(data)
  });
}

/**
 * Delete current user's account
 */
export async function deleteAccount(): Promise<ApiResponse<DeleteAccountResponse>> {
  return fetchWithAuth<DeleteAccountResponse>('/api/account', {
    method: 'DELETE'
  });
}

/**
 * Get presigned URL for profile picture upload
 */
export async function getProfilePictureUploadUrl(
  filename: string,
  contentType: string
): Promise<ApiResponse<ProfilePictureUploadUrlResponse>> {
  return fetchWithAuth<ProfilePictureUploadUrlResponse>(
    '/api/account/profile-picture/upload-url',
    {
      method: 'POST',
      body: JSON.stringify({ filename, content_type: contentType })
    }
  );
}

/**
 * Finalize profile picture upload after S3 upload is complete
 */
export async function finalizeProfilePicture(
  fileKey: string
): Promise<ApiResponse<FinalizeProfilePictureResponse>> {
  return fetchWithAuth<FinalizeProfilePictureResponse>(
    '/api/account/profile-picture/finalize',
    {
      method: 'POST',
      body: JSON.stringify({ file_key: fileKey })
    }
  );
}

/**
 * Delete current user's profile picture
 */
export async function deleteProfilePicture(): Promise<ApiResponse<void>> {
  return fetchWithAuth<void>('/api/account/profile-picture', {
    method: 'DELETE'
  });
}
